import React from 'react';
import {useTable} from 'react-table';
import {XMarkIcon} from '@heroicons/react/24/outline';
import styled from 'styled-components';
import {Pagination as UIPagination} from 'components/antd';
import Cell from './Cell';
import {
  useTasksQuery,
  Task,
  useCurrentUserQuery,
  useCurrentClientQuery,
} from 'api';
import useSearchParams from './useSearchParams';
import EditModal from './EditModal';

export default () => {
  const {page, perPage, setQuery, searchParams, query} = useSearchParams();
  const {data: {currentUser = {}} = {}} = useCurrentUserQuery({});
  const {data: {currentClient: {currentScoreContract = {}} = {}} = {}} =
    useCurrentClientQuery({
      fetchPolicy: 'cache-and-network',
    });
  const {data: {tasks: {tasks = [], pagination = {}} = {}} = {}} =
    useTasksQuery({
      variables: {
        search: searchParams,
        page,
        perPage,
      },
      fetchPolicy: 'cache-and-network',
    });

  // 選択されたタスクを管理するstate
  const [selectedTasks, setSelectedTasks] = React.useState<string[]>([]);

  // 個別のタスク選択の処理
  const handleSelectTask = (taskUuid: string) => {
    setSelectedTasks((prev) => {
      if (prev.includes(taskUuid)) {
        return prev.filter((uuid) => uuid !== taskUuid);
      } else {
        return [...prev, taskUuid];
      }
    });
  };

  const columns: any = React.useMemo(
    () => [
      {
        id: 'check',
        Header: '',
        Cell: ({row}: {row: any}) => (
          <input
            type="checkbox"
            className="form-checkbox"
            checked={selectedTasks.includes(row.original.uuid)}
            onChange={() => handleSelectTask(row.original.uuid)}
          />
        ),
        className: 'w-[40px]',
      },
      {
        id: 'taskType',
        Header: '',
        Cell: Cell.TaskType,
        className: 'w-[100px]',
      },
      {
        id: 'taskable',
        Header: '対象',
        Cell: Cell.Target,
      },
      ...(currentUser?.role === 'admin' || currentScoreContract?.isActive
        ? [
            {
              id: 'score',
              Header: <div className="text-center">スコア</div>,
              Cell: Cell.Score,
              style: {width: '96px'},
            },
          ]
        : []),
      {
        id: 'title',
        Header: 'タイトル',
        Cell: Cell.Title,
      },
      {
        id: 'status',
        Header: 'ステータス',
        Cell: Cell.Status,
        className: 'w-[100px]',
      },
      {
        id: 'priority',
        Header: '優先度',
        Cell: Cell.Priority,
        className: 'w-[80px]',
      },
      {
        id: 'due',
        Header: '日付',
        Cell: Cell.DueDate,
        className: 'w-[130px]',
      },
      {
        id: 'asignee',
        Header: 'ToDo担当者',
        Cell: Cell.Assignee,
        className: 'w-[160px]',
      },
    ],
    [currentUser?.role, currentScoreContract?.isActive, selectedTasks],
  );

  const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow} =
    useTable<Task>({columns, data: tasks || []});
  const [visible, setVisible] = React.useState(false);

  return (
    <div className="flex flex-col items-center">
      <table
        {...getTableProps()}
        className="w-full border-collapse table-fixed"
      >
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={i}>
              {headerGroup.headers.map((column, j) => {
                return (
                  <th
                    {...column.getHeaderProps([
                      {
                        className: `px-4 py-2 text-left text-xs text-gray-600 border-b border-gray-200 ${column.className || ''}`,
                        // @ts-ignore
                        style: column.style,
                      },
                    ])}
                    key={j}
                  >
                    {column.render('Header')}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {selectedTasks.length > 0 && (
          <thead>
            <tr>
              <th
                colSpan={headerGroups[0].headers.length}
                className="bg-gray-100 px-4 py-2"
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedTasks([])}
                    className="w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-800 bg-transparent cursor-pointer rounded-full hover:bg-gray-200 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                  <span className="text-gray-700">
                    {selectedTasks.length}件選択中
                  </span>
                  <button
                    className="rounded bg-primary text-white px-2 py-1 cursor-pointer hover:bg-[var(--primary-button-hover-background)]"
                    onClick={() => setVisible(true)}
                  >
                    一括編集
                  </button>
                </div>
              </th>
            </tr>
          </thead>
        )}
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={i} className="bg-white">
                {row.cells.map((cell, j) => {
                  return (
                    <td
                      {...cell.getCellProps()}
                      key={j}
                      className="px-4 py-2 text-sm border-b border-gray-200 overflow-hidden text-ellipsis first:pl-2"
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        size="small"
        showSizeChanger={false}
        showQuickJumper={false}
        total={pagination.totalCount}
        current={pagination.currentPage}
        pageSize={perPage}
        onChange={(page) => setQuery({page})}
      />
      <EditModal
        visible={visible}
        onClose={() => setVisible(false)}
        selectedTaskIds={selectedTasks}
        onSuccess={() => {
          setSelectedTasks([]);
        }}
      />
    </div>
  );
};

const Pagination = styled(UIPagination)`
  display: inline-block;
  padding: 20px 0;
  height: 65px;
  border-radius: 5px;

  li {
    width: 30px !important;
    height: 30px !important;

    a {
      width: 100%;
      height: 100%;
      line-height: 30px;
    }

    svg {
      width: 10px;
      height: 10px;
    }
  }

  .ant-pagination-item-active {
    border: none;
    background: none;

    a {
      color: var(--text-color-1);
      background: var(--border-color);
      border-radius: 100px;
    }
  }
`;
